import { deleteAolItem, getAllAolItems } from 'src/api/aol/offlineAolApi';
import useGetAolData from 'src/api/aol/useGetAolData';
import useApi from 'src/api/useApi';
import { useAolStore } from 'src/context/AolContext';
import { formatAolUrlDate } from '../aol/aolDateUtils';
import { useVesselStore } from 'src/context/VesselContext';
import { useSWService } from 'src/api/sw/useSWService';

const useSyncOfflineAolItems = () => {
    const { postApiData } = useApi();
    const { refreshRequestsContaining } = useSWService();
    const { getAolData } = useGetAolData();
    const { aolFilter } = useAolStore();
    const { selectedVessel } = useVesselStore();
    const syncAolItems = async (skipGetAolData?: boolean | null) => {
        const syncedItems: Record<number, number> = {};

        try {
            const aolItems = await getAllAolItems();
            aolItems.sort((a, b) => a.lastUpdated - b.lastUpdated);

            for (const item of aolItems) {
                const itemKey = item.noonReportId;
                let allRequestsSuccessful = true;

                try {
                    const requests = [...(item.saveNoonReportSectionRequests ?? []), ...(item?.updateSectionStatusRequests ?? [])];

                    if (item.deleteNoonReportRequest) {
                        const response = await postApiData({
                            fullUrl: item.deleteNoonReportRequest.url,
                            data: item.deleteNoonReportRequest.body,
                            skipRefreshCacheEndpoints: true,
                        });
                        if (!response) {
                            allRequestsSuccessful = false;
                        }
                    } else {
                        if (item.addNoonReportRequest) {
                            const response = await postApiData({
                                fullUrl: item.addNoonReportRequest.url,
                                data: item.addNoonReportRequest.body,
                                skipRefreshCacheEndpoints: true,
                            });
                            if (response?.metadata?.noonReportId) {
                                syncedItems[itemKey] = response.metadata.noonReportId;
                                item.noonReportId = response.metadata.noonReportId;
                            } else {
                                allRequestsSuccessful = false;
                            }
                        } else if (item.noonReportId < 0) {
                            // Invalid state: offline-created report (negative ID) without addNoonReportRequest
                            // Cannot sync section data without first creating the noon report
                            console.error(
                                `Cannot sync offline noon report sections: noonReportId=${item.noonReportId} has no addNoonReportRequest. ` +
                                    'This item will be removed to prevent repeated sync failures.',
                                item
                            );
                            // Delete the corrupted item to prevent repeated sync failures
                            await deleteAolItem(itemKey);
                            continue;
                        }

                        if (allRequestsSuccessful) {
                            for (const request of requests) {
                                if (request.response.noonReportId !== item.noonReportId) {
                                    if (request.body?.noonReportId) {
                                        request.body.noonReportId = item.noonReportId;
                                    }
                                    if (request.body?.sectionData?.noonReportId) {
                                        request.body.sectionData.noonReportId = item.noonReportId;
                                    }
                                    if (request.url.includes('noonReportId=')) {
                                        request.url = request.url.replace(
                                            `noonReportId=${request.response.noonReportId}`,
                                            `noonReportId=${item.noonReportId}`
                                        );
                                    }
                                }
                                const sectionResponse = await postApiData({
                                    fullUrl: request.url,
                                    data: request.body,
                                    skipRefreshCacheEndpoints: true,
                                });
                                if (!sectionResponse) {
                                    allRequestsSuccessful = false;
                                    break;
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error('An unexpected error occurred while syncing item.', item, error);
                    allRequestsSuccessful = false;
                }

                if (allRequestsSuccessful) {
                    await deleteAolItem(itemKey);
                }
            }

            if (!skipGetAolData) {
                await getAolData(selectedVessel.imoNumber, formatAolUrlDate(aolFilter.fromDate), formatAolUrlDate(aolFilter.toDate), true);
                await refreshRequestsContaining(['getNoonReportsForShip', 'getNoonReportStatuses', 'getNoonReportSection', 'getNoonReportComments']);
            }
            return syncedItems;
        } catch (error) {
            console.error(error);
        }
    };

    return {
        syncAolItems,
    };
};

export default useSyncOfflineAolItems;
